// Single source of truth for the app's top-level screen universe. PURE — no
// React, no JSX, no Firestore — so it bundles into the node test harness the
// same way hashRoute.js does. Consumed by:
//   • hashRoute.js   — path serialize/parse (query/peek stay in hashRoute.js)
//   • Sidebar.jsx    — active-state highlight (isActive)
//   • Taskboard.jsx  — render-ladder dispatch (matchRoute -> renderers[id])
//   • searchIndex.js — Cmd-K destinations (SCREENS)
// See docs/superpowers/specs/2026-07-28-route-nav-registry-refactor-design.md.
//
// Each entry owns one top-level screen:
//   id        stable key (also the render-map key in Taskboard.jsx)
//   heads     path head token(s) this entry owns (the first path segment)
//   match     (route) => bool — render-ladder dispatch + default active-state
//   isActive  (route) => bool — sidebar highlight; defaults to `match`
//   toPath    (route) => string — route -> path string (no query part)
//   fromParts (head, rest) => route — [head, ...rest] segments -> route object
//   screen    palette/sidebar destination metadata, or null if not a destination
//   fullBleed true if this screen renders edge-to-edge (no centered max-width
//             column); consumed by Taskboard.jsx's tb-content layout via isFullBleed

export const ROUTES = [
  {
    id: 'home', heads: [], fullBleed: true,
    match: (r) => r.type === 'home',
    toPath: () => '/',
    fromParts: () => ({ type: 'home' }),
    screen: { title: 'Home', keywords: 'dashboard' },
  },
  {
    id: 'mytasks', heads: ['my-tasks'],
    match: (r) => r.type === 'mytasks',
    toPath: () => '/my-tasks',
    fromParts: () => ({ type: 'mytasks' }),
    screen: { title: 'My Tasks', keywords: 'todo assignments' },
  },
  {
    id: 'contacts', heads: ['contacts'], fullBleed: true,
    match: (r) => r.type === 'crm' && r.object === 'contacts',
    toPath: (r) => (r.recordId ? `/contacts/${r.recordId}` : '/contacts'),
    fromParts: (_h, rest) => (rest[0] ? { type: 'crm', object: 'contacts', recordId: rest[0] } : { type: 'crm', object: 'contacts' }),
    screen: { title: 'Contacts', keywords: 'people crm relationships' },
  },
  {
    id: 'companies', heads: ['companies'], fullBleed: true,
    match: (r) => r.type === 'crm' && r.object === 'companies',
    toPath: (r) => (r.recordId ? `/companies/${r.recordId}` : '/companies'),
    fromParts: (_h, rest) => (rest[0] ? { type: 'crm', object: 'companies', recordId: rest[0] } : { type: 'crm', object: 'companies' }),
    screen: { title: 'Companies', keywords: 'orgs crm relationships' },
  },
  {
    // Wave 2: `deals` folded into engagements. The head is kept ONLY so old
    // bookmarks and Wave 1 drill-down links resolve to something real instead of
    // 404-ing; it is not a destination (screen: null) and owns no render.
    id: 'deals-legacy', heads: ['deals'], fullBleed: true,
    match: () => false,
    fromParts: () => ({ type: 'engagements', board: 'speaking' }),
    toPath: () => '/speaking',
    screen: null,
  },
  {
    id: 'forms', heads: ['forms'], fullBleed: true,
    match: (r) => r.type === 'forms',
    toPath: (r) => {
      if (!r.formId) return '/forms'
      return r.tab === 'submissions' ? `/forms/${r.formId}/submissions` : `/forms/${r.formId}`
    },
    fromParts: (_h, rest) => {
      if (!rest[0]) return { type: 'forms' }
      return rest[1] === 'submissions'
        ? { type: 'forms', formId: rest[0], tab: 'submissions' }
        : { type: 'forms', formId: rest[0] }
    },
    screen: { title: 'Forms', keywords: 'submissions leads quarantine analytics conversion' },
  },
  {
    id: 'advisory', heads: ['advisory'], fullBleed: true,
    match: (r) => r.type === 'engagements' && r.board === 'advisory',
    toPath: (r) => (r.recordId ? `/advisory/${r.recordId}` : '/advisory'),
    fromParts: (_h, rest) => (rest[0] ? { type: 'engagements', board: 'advisory', recordId: rest[0] } : { type: 'engagements', board: 'advisory' }),
    screen: { title: 'Advisory', keywords: 'engagements consulting signal audits retainer' },
  },
  {
    id: 'speaking', heads: ['speaking'], fullBleed: true,
    // match is the engagements catch-all (default board = speaking), preserving
    // the old serializePath default; isActive stays exact so the sidebar
    // highlights Speaking only for board === 'speaking'.
    // NOTE: the route key stays `board` while its VALUE is an engagement type
    // ('advisory' | 'speaking' | 'pr'). Renaming it would invalidate every route
    // object already persisted in caravan.nav, breaking "remember where you were".
    match: (r) => r.type === 'engagements' && r.board !== 'pr' && r.board !== 'advisory',
    isActive: (r) => r.type === 'engagements' && r.board === 'speaking',
    toPath: (r) => (r.recordId ? `/speaking/${r.recordId}` : '/speaking'),
    fromParts: (_h, rest) => (rest[0] ? { type: 'engagements', board: 'speaking', recordId: rest[0] } : { type: 'engagements', board: 'speaking' }),
    screen: { title: 'Speaking', keywords: 'engagements talks' },
  },
  {
    id: 'pr', heads: ['pr'], fullBleed: true,
    match: (r) => r.type === 'engagements' && r.board === 'pr',
    toPath: (r) => (r.recordId ? `/pr/${r.recordId}` : '/pr'),
    fromParts: (_h, rest) => (rest[0] ? { type: 'engagements', board: 'pr', recordId: rest[0] } : { type: 'engagements', board: 'pr' }),
    screen: { title: 'PR & Media', keywords: 'press media engagements' },
  },
  {
    id: 'team', heads: ['team'],
    match: (r) => r.type === 'team',
    toPath: () => '/team',
    fromParts: () => ({ type: 'team' }),
    screen: { title: 'Team', keywords: 'people directory' },
  },
  {
    id: 'tools', heads: ['tools'],
    match: (r) => r.type === 'tools',
    toPath: () => '/tools',
    fromParts: () => ({ type: 'tools' }),
    screen: { title: 'Tools & Links', keywords: 'links resources' },
  },
  {
    id: 'playbook', heads: ['playbook'],
    match: (r) => r.type === 'playbook',
    toPath: () => '/playbook',
    fromParts: () => ({ type: 'playbook' }),
    screen: { title: 'Playbook', keywords: 'docs guide handbook' },
  },
  {
    id: 'settings', heads: ['settings'],
    match: (r) => r.type === 'settings',
    toPath: () => '/settings',
    fromParts: () => ({ type: 'settings' }),
    screen: { title: 'Settings', keywords: 'preferences account' },
  },
  {
    id: 'project', heads: ['project'],
    match: (r) => r.type === 'project',
    toPath: (r) => (r.projectId ? `/project/${r.projectId}` : '/'),
    fromParts: (_h, rest) => (rest[0] ? { type: 'project', projectId: rest[0] } : { type: 'home' }),
    screen: null, // projects are their own sidebar section, not a palette screen
  },
]

const BY_HEAD = {}
for (const e of ROUTES) for (const h of e.heads) BY_HEAD[h] = e

// route -> path string (no query). Mirrors the old hashRoute serializePath:
// no matching entry (object-less crm, unknown type, malformed) -> '/'.
export function serializePath(route) {
  if (!route || !route.type) return '/'
  const e = ROUTES.find((x) => x.match(route))
  return e ? e.toPath(route) : '/'
}

// path segments -> route. Empty -> home; unknown head -> home.
export function parsePath(parts) {
  if (!parts || parts.length === 0) return { type: 'home' }
  const [head, ...rest] = parts
  const e = BY_HEAD[head]
  return e ? e.fromParts(head, rest) : { type: 'home' }
}

// render-ladder dispatch. Never null: unmatched (loading/unknown) -> project,
// matching the old ternary's trailing `else` arm.
export function matchRoute(route) {
  return ROUTES.find((x) => x.match(route)) || ROUTES.find((x) => x.id === 'project')
}

// layout: does this route's screen render edge-to-edge (no centered max-width
// column)? Derives from the matched entry's fullBleed flag. Uses matchRoute so
// the fallback (project) is never full-bleed, matching the old type-list check.
export function isFullBleed(route) {
  return !!matchRoute(route).fullBleed
}

// sidebar highlight for a given entry id.
export function isActive(id, route) {
  const e = ROUTES.find((x) => x.id === id)
  if (!e) return false
  return (e.isActive || e.match)(route)
}

// Cmd-K / sidebar destinations, same shape + order as the old hand-written list.
export const SCREENS = ROUTES.filter((e) => e.screen).map((e) => ({
  key: e.id,
  group: 'screen',
  title: e.screen.title,
  keywords: e.screen.keywords,
  route: e.fromParts(e.heads[0], []),
}))
